import React, { useRef, useState } from 'react';
import { ImagePlus, Loader2, RefreshCw } from 'lucide-react';
import { uploadImage } from '../../lib/upload';

export default function CoverUpload({ value, onChange }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const busy = progress !== null;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setProgress(0);
    try {
      const res = await uploadImage(file, setProgress);
      onChange(res.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setProgress(null);
    }
  };

  return (
    <div data-testid="cover-upload">
      <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2">Cover photo</label>
      <div className="relative aspect-[16/9] rounded-lg overflow-hidden border border-dashed border-white/15 bg-[#0f0f0f]">
        {value ? (
          <img src={value} alt="Cover preview" className="w-full h-full object-cover" data-testid="cover-preview" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-[#5a5a5a] text-sm gap-2">
            <ImagePlus className="w-8 h-8" />No cover photo yet
          </div>
        )}
        {busy && <UploadOverlay progress={progress} testid="cover-upload-progress" />}
      </div>
      <div className="flex items-center gap-4 mt-3">
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid="cover-upload-btn"
          className="inline-flex items-center gap-2 border border-white/15 rounded-full px-5 py-2.5 text-sm text-[#f4efe6] hover:border-[#00b8ff] hover:text-[#00b8ff] transition-colors duration-200 disabled:opacity-50"
        >
          {value ? <RefreshCw className="w-4 h-4" /> : <ImagePlus className="w-4 h-4" />}
          {value ? 'Replace photo' : 'Upload photo'}
        </button>
        <span className="text-xs text-[#7a7a7a]">JPG, PNG, WebP or GIF · resized to 2000px wide WebP</span>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="cover-file-input" />
      {error && <p className="text-sm text-red-400 mt-2" data-testid="cover-upload-error">{error}</p>}
    </div>
  );
}

export function UploadOverlay({ progress, testid }) {
  return (
    <div className="absolute inset-0 bg-black/75 flex flex-col items-center justify-center gap-3" data-testid={testid}>
      <Loader2 className="w-6 h-6 text-[#00b8ff] animate-spin" />
      <span className="text-sm text-[#f4efe6]">{progress < 100 ? `Uploading ${progress}%` : 'Optimizing photo…'}</span>
      <div className="w-48 h-1 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full bg-[#00b8ff] transition-[width] duration-200" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}
